import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { partnersApi, Partner } from '../../api/partners';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';

const DD_STATUS_LABELS: Record<string, string> = {
  prospective: 'Prospective', dd_draft: 'DD Draft', submitted: 'Submitted', under_review: 'Under Review',
  additional_info_required: 'Additional Info Required', approved: 'Approved', approved_with_conditions: 'Approved with Conditions',
  rejected: 'Rejected', renewal_due: 'Renewal Due', expired: 'Expired',
};
const ORG_TYPE_LABELS: Record<string, string> = {
  national_ngo: 'National NGO', international_ngo: 'International NGO', cbo: 'CBO', cso: 'CSO',
  foundation: 'Foundation', government: 'Government', private_sector: 'Private Sector', other: 'Other',
};

/** Partner Registry - the professional list view (spec section 4). Search, filter by status/risk/type, and the entry point for starting due diligence on a new partner. */
export function PartnersListPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [ddStatus, setDdStatus] = useState('');
  const [riskRating, setRiskRating] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const partnersQuery = useQuery({ queryKey: ['partners', search, ddStatus, riskRating], queryFn: () => partnersApi.list({ search: search || undefined, ddStatus: ddStatus || undefined, riskRating: riskRating || undefined }) });

  const { register, handleSubmit, reset } = useForm<{ legalName: string; acronym: string; organizationType: string; country: string }>();
  const createMutation = useMutation({
    mutationFn: (values: { legalName: string; acronym: string; organizationType: string; country: string }) =>
      partnersApi.create({ legalName: values.legalName, acronym: values.acronym || undefined, organizationType: values.organizationType, country: values.country || undefined }),
    onSuccess: (result) => {
      showSuccess(`Partner registered - ${result.data.partnerReference}`);
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      setShowCreateForm(false);
      reset();
      navigate(`/partners/${result.data.id}`);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to register partner.'),
  });

  return (
    <div className="page">
      <PageHeader title="Partner Registry" description="Prospective and current institutional partners, their due diligence status, and risk rating." />

      <div className="panel">
        <div className="panel-body">
          <div className="inline-form">
            <label>Search<input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Legal name, acronym, or reference" /></label>
            <label>
              Due Diligence Status
              <select value={ddStatus} onChange={(e) => setDdStatus(e.target.value)}>
                <option value="">- all -</option>
                {Object.entries(DD_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label>
              Risk Rating
              <select value={riskRating} onChange={(e) => setRiskRating(e.target.value)}>
                <option value="">- all -</option>
                <option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </label>
            <button onClick={() => setShowCreateForm((v) => !v)}>{showCreateForm ? 'Cancel' : 'Register New Partner'}</button>
          </div>

          {showCreateForm && (
            <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))} style={{ marginTop: 12, borderTop: '0.5px solid var(--border)', paddingTop: 12 }}>
              <label>Legal Name<input {...register('legalName', { required: true })} /></label>
              <label>Acronym<input {...register('acronym')} /></label>
              <label>
                Organization Type
                <select {...register('organizationType', { required: true })}>
                  <option value="">- choose -</option>
                  {Object.entries(ORG_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label>Country<input {...register('country')} /></label>
              <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Registering…' : 'Register'}</button>
            </form>
          )}
        </div>
      </div>

      <div className="panel">
        <SectionHeader title="Partners" />
        <div className="panel-body">
          {partnersQuery.data && (
            <DataTable<Partner>
              rows={partnersQuery.data.data}
              rowKey={(p) => p.id}
              emptyMessage="No partners registered yet."
              columns={[
                { header: 'Partner ID', render: (p) => <Link to={`/partners/${p.id}`}>{p.partner_reference}</Link> },
                { header: 'Legal Name', render: (p) => p.acronym ? `${p.legal_name} (${p.acronym})` : p.legal_name },
                { header: 'Type', render: (p) => ORG_TYPE_LABELS[p.organization_type] ?? p.organization_type },
                { header: 'Location', render: (p) => [p.state, p.country].filter(Boolean).join(', ') || '-' },
                { header: 'DD Status', render: (p) => <StatusBadge status={p.due_diligence_status} label={DD_STATUS_LABELS[p.due_diligence_status]} /> },
                { header: 'Risk', render: (p) => p.current_risk_rating ? <StatusBadge status={p.current_risk_rating} label={p.current_risk_rating.charAt(0).toUpperCase() + p.current_risk_rating.slice(1)} /> : '-' },
                { header: 'Last Review', render: (p) => p.last_review_date ?? '-' },
                { header: 'Next Review', render: (p) => p.next_review_date ?? '-' },
                { header: 'Partnership', render: (p) => <StatusBadge status={p.partnership_status} label={p.partnership_status.charAt(0).toUpperCase() + p.partnership_status.slice(1)} /> },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
}
